import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import config from '../config';
import S3StorageMetrics from './S3StorageMetrics';
import PaymentInfo from './PaymentInfo';
import TrialBanner from './TrialBanner';
import useCache from '../hooks/useCache';
import CacheIndicator from './CacheIndicator';
import {
  AppLayout,
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Alert,
  Spinner,
  Badge,
  Button,
  Tabs,
  ContentLayout,
  ExpandableSection,
  ProgressBar
} from '@cloudscape-design/components';

const fetchCostData = async () => {
  const session = await fetchAuthSession();
  
  const lambda = new LambdaClient({
    region: config.Region,
    credentials: session.credentials
  });

  const command = new InvokeCommand({
    FunctionName: config.CostTrackingFunctionArn,
    Payload: JSON.stringify({})
  });

  const response = await lambda.send(command);
  const result = JSON.parse(new TextDecoder().decode(response.Payload));
  
  if (result.statusCode === 200) {
    return JSON.parse(result.body);
  } else {
    throw new Error(result.body || 'Failed to fetch cost data');
  }
};

function UsageCosts() {
  const [activeTabId, setActiveTabId] = useState('overview');
  const [paymentCache, setPaymentCache] = useState({});
  const { data: costData, loading: isLoading, error, cachedAt, isCached, refresh } = useCache('usage-costs', fetchCostData);

  const activeCache = activeTabId === 'payment' && paymentCache.cachedAt
    ? paymentCache
    : { cachedAt, isCached, loading: isLoading, refresh };

  const formatCurrency = (amount, showLessThanForZero = false) => {
    if (amount === null || amount === undefined) return '$0.00';
    if (amount === 0 && showLessThanForZero) return '< $0.01';
    if (amount === 0) return '$0.00';
    if (amount > 0 && amount < 0.01) return '< $0.01';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0s';
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    
    return parts.join(' ');
  };

  if (isLoading) {
    return (
      <AppLayout
        headerSelector=".sticky-nav"
        navigationHide
        toolsHide
        content={
          <Container>
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
              <Box variant="p" padding={{ top: 's' }}>
                Loading your cost data...
              </Box>
            </Box>
          </Container>
        }
      />
    );
  }

  if (error && !costData) {
    return (
      <AppLayout
        headerSelector=".sticky-nav"
        navigationHide
        toolsHide
        content={
          <Container>
            <SpaceBetween size="l">
              <Header variant="h1">Usage & Costs</Header>
              <Alert type="error">
                {error}
              </Alert>
              <Button onClick={refresh}>Retry</Button>
            </SpaceBetween>
          </Container>
        }
      />
    );
  }

  // Calculate billing period dates
  const getBillingPeriod = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      startFormatted: formatDate(start.toISOString()),
      endFormatted: formatDate(end.toISOString())
    };
  };

  const billingPeriod = getBillingPeriod();

  // Calculate total costs
  const getTotalCosts = () => {
    const transcription = costData?.transcription?.totalCost || 0;
    const bedrockLlm = costData?.['bedrock-llm']?.totalCost || 0;
    const knowledgeBase = costData?.knowledgeBase?.totalCost || 0;
    const s3Total = costData?.s3Storage?.costs?.total || 0;
    
    return {
      transcription,
      bedrockLlm,
      knowledgeBase,
      s3: s3Total,
      total: transcription + bedrockLlm + knowledgeBase + s3Total
    };
  };

  const totalCosts = costData ? getTotalCosts() : null;

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Overview Tab Content
  const overviewContent = totalCosts && (
    <SpaceBetween size="l">
      {/* Billing Period Header */}
      <Container>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Current Billing Period</Box>
            <Box variant="h2">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</Box>
            <Box variant="small" color="text-body-secondary">
              {billingPeriod.startFormatted} - {billingPeriod.endFormatted}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Total Cost</Box>
            <Box variant="h1" color={totalCosts.total > 0 ? "text-status-info" : "text-status-success"}>
              {formatCurrency(totalCosts.total)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              {billingPeriod.startFormatted} - {formatDate(new Date())}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Credit Remaining</Box>
            <Box variant="h2" color="text-status-success">
              {formatCurrency(costData?.billing?.creditRemaining || 0)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              After current month's usage
            </Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Cost Breakdown by Service */}
      <Container>
        <Header variant="h2">Cost Breakdown by Service</Header>
        <div style={{ marginTop: '16px' }}>
          <SpaceBetween size="l">

          {/* Storage Overview */}
          <ExpandableSection 
            headerText="💾 Recordings" 
            variant="container"
            headerDescription="Updated daily"
          >
            <Container variant='embedded'>
              <SpaceBetween size="m">
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Total Cost</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(costData?.s3Storage?.costs?.total || 0)}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Storage Cost</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(costData?.s3Storage?.costs?.storage || 0)}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Requests Cost</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(costData?.s3Storage?.costs?.requests || 0)}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Transfer Cost</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(costData?.s3Storage?.costs?.transfer || 0)}
                    </Box>
                  </div>
                </ColumnLayout>
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Current Storage</Box>
                    <Box variant="h4">{formatBytes(costData?.s3Storage?.storage?.currentBytes || 0)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Total Objects</Box>
                    <Box variant="h4">{costData?.s3Storage?.storage?.currentObjects || 0}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Total Requests</Box>
                    <Box variant="h4">{(costData?.s3Storage?.requests?.total || 0).toLocaleString()}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Data Transfer</Box>
                    <Box variant="h4">
                      {formatBytes(costData?.s3Storage?.bandwidth?.downloaded || 0)} ↓ | {formatBytes(costData?.s3Storage?.bandwidth?.uploaded || 0)} ↑
                    </Box>
                  </div>
                </ColumnLayout>
              </SpaceBetween>
            </Container>
          </ExpandableSection>

          {/* Transcription Overview */}
          <ExpandableSection 
            headerText="🎤 Transcription" 
            variant="container"
            headerDescription="Real-time"
          >
            <Container variant='embedded'>
              <SpaceBetween size="m">
                <ColumnLayout columns={3} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Total Cost</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(costData?.transcription?.totalCost || 0)}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Jobs Completed</Box>
                    <Box variant="h3">{costData?.transcription?.totalJobs || 0}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Audio Processed</Box>
                    <Box variant="h3">{formatDuration(costData?.transcription?.totalMinutes || 0)}</Box>
                  </div>
                </ColumnLayout>
                
                <SpaceBetween size="s">
                  <Container>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Badge color="blue">Standard</Badge>
                    </SpaceBetween>
                    <ColumnLayout columns={3} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">Cost</Box>
                        <Box variant="h4" color="text-status-info">
                          {formatCurrency(costData?.transcription?.standard?.cost || 0)}
                        </Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Jobs</Box>
                        <Box variant="h4">{costData?.transcription?.standard?.jobs || 0}</Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Duration</Box>
                        <Box variant="h4">{formatDuration(costData?.transcription?.standard?.minutes || 0)}</Box>
                      </div>
                    </ColumnLayout>
                  </Container>
                  
                  <Container>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Badge color="green">Instant</Badge>
                    </SpaceBetween>
                    <ColumnLayout columns={3} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">Cost</Box>
                        <Box variant="h4" color="text-status-info">
                          {formatCurrency(costData?.transcription?.instant?.cost || 0)}
                        </Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Jobs</Box>
                        <Box variant="h4">{costData?.transcription?.instant?.jobs || 0}</Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Duration</Box>
                        <Box variant="h4">{formatDuration(costData?.transcription?.instant?.minutes || 0)}</Box>
                      </div>
                    </ColumnLayout>
                  </Container>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          </ExpandableSection>

          {/* AI Summarization Overview */}
          <ExpandableSection 
            headerText="🤖 AI Summarization" 
            variant="container"
            headerDescription="Real-time"
          >
            <Container variant='embedded'>
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Total Cost</Box>
                  <Box variant="h3" color="text-status-info">
                    {formatCurrency(costData?.['bedrock-llm']?.totalCost || 0)}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Summaries Generated</Box>
                  <Box variant="h3">{costData?.['bedrock-llm']?.totalRequests || 0}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Tokens Processed</Box>
                  <Box variant="h3">
                    {((costData?.['bedrock-llm']?.totalInputTokens || 0) + (costData?.['bedrock-llm']?.totalOutputTokens || 0)).toLocaleString()}
                  </Box>
                </div>
              </ColumnLayout>
            </Container>
          </ExpandableSection>

          {/* Knowledge Base Overview */}
          <ExpandableSection 
            headerText="🧠 Knowledge Base" 
            variant="container"
            headerDescription="Real-time (Storage & Transfer updated daily)"
          >
            <Container variant='embedded'>
              <SpaceBetween size="m">
                <ColumnLayout columns={3} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Total Cost</Box>
                    <Box variant="h3" color="text-status-info">
                      {formatCurrency(costData?.knowledgeBase?.totalCost || 0)}
                    </Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Current Storage</Box>
                    <Box variant="h3">{formatBytes(costData?.knowledgeBase?.storage?.storage?.currentBytes || 0)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Total Objects</Box>
                    <Box variant="h3">{costData?.knowledgeBase?.storage?.storage?.currentObjects || 0}</Box>
                  </div>
                </ColumnLayout>

                <Container>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Badge color="blue">Storage & Transfer</Badge>
                  </SpaceBetween>
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Storage Cost</Box>
                      <Box variant="h4" color="text-status-info">
                        {formatCurrency(costData?.s3StorageKB?.costs?.storage || 0)}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Requests</Box>
                      <Box variant="h4">{(costData?.s3StorageKB?.requests?.total || 0).toLocaleString()}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Data Transfer</Box>
                      <Box variant="h4">
                        {formatBytes(costData?.s3StorageKB?.bandwidth?.downloaded || 0)} ↓ | {formatBytes(costData?.s3StorageKB?.bandwidth?.uploaded || 0)} ↑
                      </Box>
                    </div>
                  </ColumnLayout>
                </Container>

                <Container>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Badge color="blue">Ingestion</Badge>
                  </SpaceBetween>
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Cost</Box>
                      <Box variant="h4" color="text-status-info">
                        {formatCurrency(costData?.knowledgeBase?.ingestion?.totalCost || 0)}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Documents Ingested</Box>
                      <Box variant="h4">{costData?.knowledgeBase?.ingestion?.documentsIngested || 0}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Embedding Tokens</Box>
                      <Box variant="h4">{(costData?.knowledgeBase?.ingestion?.embeddingTokens || 0).toLocaleString()}</Box>
                    </div>
                  </ColumnLayout>
                </Container>

                <Container>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Badge color="blue">Search</Badge>
                  </SpaceBetween>
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Cost</Box>
                      <Box variant="h4" color="text-status-info">
                        {formatCurrency(costData?.knowledgeBase?.query?.totalCost || 0)}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Queries</Box>
                      <Box variant="h4">{costData?.knowledgeBase?.query?.queries || 0}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Embedding Tokens</Box>
                      <Box variant="h4">{(costData?.knowledgeBase?.query?.embeddingTokens || 0).toLocaleString()}</Box>
                    </div>
                  </ColumnLayout>
                </Container>

                <Container>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Badge color="blue">AI Assistant</Badge>
                  </SpaceBetween>
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Cost</Box>
                      <Box variant="h4" color="text-status-info">
                        {formatCurrency(costData?.knowledgeBase?.rag?.totalCost || 0)}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Queries</Box>
                      <Box variant="h4">{costData?.knowledgeBase?.rag?.queries || 0}</Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Tokens Processed</Box>
                      <Box variant="h4">{((costData?.knowledgeBase?.rag?.inputTokens || 0) + (costData?.knowledgeBase?.rag?.outputTokens || 0)).toLocaleString()}</Box>
                    </div>
                  </ColumnLayout>
                </Container>
              </SpaceBetween>
            </Container>
          </ExpandableSection>
        </SpaceBetween>
        </div>
      </Container>
    </SpaceBetween>
  );

  // Transcription Tab Content
  const transcriptionData = costData?.transcription || {};
  const transcriptionContent = (
    <SpaceBetween size="l">
      <ColumnLayout columns={3} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Total Transcription Cost</Box>
          <Box variant="h2" color={transcriptionData.totalCost > 0 ? "text-status-info" : "text-status-success"}>
            {formatCurrency(transcriptionData.totalCost || 0)}
          </Box>
          <Box variant="small" color="text-body-secondary">
            This billing period
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Total Jobs</Box>
          <Box variant="h2">{transcriptionData.totalJobs || 0}</Box>
          <Box variant="small" color="text-body-secondary">
            Completed successfully
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Total Duration</Box>
          <Box variant="h2">{(transcriptionData.totalMinutes || 0).toFixed(1)} min</Box>
          <Box variant="small" color="text-body-secondary">
            Audio processed
          </Box>
        </div>
      </ColumnLayout>

      {!transcriptionData.totalCost || transcriptionData.totalCost === 0 ? (
        <Alert type="success">
          <Box variant="strong">No transcription costs yet!</Box> Start recording to see your usage appear here.
        </Alert>
      ) : (
        <Alert type="info">
          <Box variant="strong">Two transcription options available:</Box> Basic (AWS Transcribe) and Advanced (Whisper GPU) with different pricing and accuracy levels.
        </Alert>
      )}

      <Container>
        <Header variant="h3">Basic Transcription</Header>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Cost</Box>
            <Box variant="h3" color="text-status-info">
              {formatCurrency(transcriptionData.basic?.cost || 0)}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Jobs</Box>
            <Box variant="h3">{transcriptionData.basic?.jobs || 0}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Duration</Box>
            <Box variant="h3">{(transcriptionData.basic?.minutes || 0).toFixed(1)} min</Box>
          </div>
        </ColumnLayout>
        <Box variant="small" color="text-body-secondary" padding={{ top: 's' }}>
          Fast processing, good for general use.
        </Box>
      </Container>

      <Container>
        <Header variant="h3">Advanced Transcription</Header>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Cost</Box>
            <Box variant="h3" color="text-status-info">
              {formatCurrency(transcriptionData.advanced?.cost || 0)}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Jobs</Box>
            <Box variant="h3">{transcriptionData.advanced?.jobs || 0}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Duration</Box>
            <Box variant="h3">{(transcriptionData.advanced?.minutes || 0).toFixed(1)} min</Box>
          </div>
        </ColumnLayout>
        <Box variant="small" color="text-body-secondary" padding={{ top: 's' }}>
          Higher accuracy, better for technical content and accents.
        </Box>
      </Container>

      <Container>
        <Header variant="h3">Cost Optimization Tips</Header>
        <SpaceBetween size="s">
          <Box>• Basic is faster and good for general content</Box>
          <Box>• Advanced provides higher accuracy for technical content and accents</Box>
          <Box>• Keep recordings focused and concise to minimize processing time</Box>
          <Box>• Remove long periods of silence before uploading</Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );

  return (
    <AppLayout
      headerSelector=".sticky-nav"
      notifications={<TrialBanner />}
      navigationHide
      toolsHide
      disableBodyScroll={false}
      content={
        <ContentLayout
          header={
            <Header 
              variant="h1"
              actions={
                <CacheIndicator cachedAt={activeCache.cachedAt} isCached={activeCache.isCached} loading={activeCache.loading} onRefresh={activeCache.refresh} />
              }
            >
              Usage & Costs
            </Header>
          }
        >
          <Container variant="embedded">
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
              tabs={[
                {
                  id: 'overview',
                  label: 'Overview',
                  content: overviewContent
                },
                {
                  id: 'payment',
                  label: 'Payment & Invoices',
                  content: <PaymentInfo onCacheReady={setPaymentCache} />
                }
              ]}
            />
          </Container>
        </ContentLayout>
      }
    />
  );
}

export default UsageCosts;
